import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PaymentForm, { PaymentFormRef, PaymentFormValues } from './PaymentForm';

export type CreatePaymentProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: PaymentFormValues) => Promise<void>;
  paymentModel?: string;
  hideDates?: boolean;
};

const CreatePaymentModal: React.FC<CreatePaymentProps> = ({
  isOpen,
  toggle,
  onSubmit,
  paymentModel,
  hideDates,
}: CreatePaymentProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = payrollCountry === UNITED_KINGDOM;

  const formRef = useRef<PaymentFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: PaymentFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['paymentSetting.payment.addNewPayment']}
      isOpen={isOpen}
      toggle={toggle}
      destroyOnClose
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      <PaymentForm
        formRef={formRef}
        onSubmit={handleSubmit}
        paymentModel={paymentModel}
        hideDates={hideDates}
        defaultValues={{
          includeInCisCalculation: false,
          beforeTax: isUK ? false : true,
          employeePRSICanBeDeducted: true,
          employerPRSICanBeDeducted: true,
          uscCanBeDeducted: true,
          ascCanBeDeducted: true,
          pensionSchemaDeductionCanBeDeducted: true,
          beforeNI: false,
          beforePension: false,
        }}
      />
    </AppModalContainer>
  );
};

export default CreatePaymentModal;
